import React, { useEffect, useMemo, useState } from 'react';
import axiosInstance from '../../utils/axiosInstance';
import AuthService from '../../services/authService';
import { formatStatusLabel, deliveryStatusLabel } from '../../utils/batchStudentStatusHelpers';
import { formatDate, formatScheduleType, daysSince } from '../../utils/productSubmissionHelpers';
import { attemptLabel, formatMarks } from '../../utils/diplomaCompletionHelpers';

const PRINT_STYLES = `
@media print {
  @page { size: A4 landscape; margin: 14mm 10mm; }
  .print-hide { display: none !important; }
  body { background: #fff !important; }
  #diploma-completion-print table { page-break-inside: auto; }
  #diploma-completion-print tr { page-break-inside: avoid; break-inside: avoid; }
  #diploma-completion-print .print-course-block:not(:first-child) { page-break-before: always; break-before: page; }
  #diploma-completion-print .print-signoff-group { page-break-inside: avoid; break-inside: avoid; }
}
`;

function attemptStyle(attempt) {
  if (attempt === '2nd attempt') return { background: '#fef3c7', color: '#92400e' };
  if (attempt === '3rd attempt') return { background: '#fee2e2', color: '#991b1b' };
  return { background: '#dcfce7', color: '#166534' };
}

function BatchPhaseSheetTable({ batch }) {
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <p className="text-sm font-semibold text-gray-800">
          {batch.batchInternalName || batch.whatsappGroupName || batch.batchName || 'Batch'}
          <span className="ml-2 font-normal text-gray-500">({formatStatusLabel(batch.status)})</span>
        </p>
        <span className="text-xs text-gray-500">
          {formatDate(batch.batchStartingDate)} – {formatDate(batch.batchEndingDate)}
        </span>
      </div>
      <table className="w-full border-collapse text-[10px]">
        <thead>
          <tr className="border border-gray-400 bg-gray-100">
            <th className="border border-gray-400 px-1.5 py-1 text-left">#</th>
            <th className="border border-gray-400 px-1.5 py-1 text-left">University ID</th>
            <th className="border border-gray-400 px-1.5 py-1 text-left">Student ID</th>
            <th className="border border-gray-400 px-1.5 py-1 text-left">Reg. No.</th>
            <th className="border border-gray-400 px-1.5 py-1 text-left">Name</th>
            {batch.phases.map((phase) => (
              <th key={phase.phaseId} className="border border-gray-400 px-1 py-1 text-center" title={phase.phaseName}>
                {phase.phaseName}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {batch.students.map((s, idx) => (
            <tr key={`${s.studentId}-${s.registrationNumber}`}>
              <td className="border border-gray-400 px-1.5 py-1">{idx + 1}</td>
              <td className="border border-gray-400 px-1.5 py-1">{s.universityID || '—'}</td>
              <td className="border border-gray-400 px-1.5 py-1">{s.studentId || '—'}</td>
              <td className="border border-gray-400 px-1.5 py-1">{s.registrationNumber || '—'}</td>
              <td className="border border-gray-400 px-1.5 py-1">
                <div>{s.studentName || '—'}</div>
                <div className="text-gray-500">{deliveryStatusLabel(s.deliveryStatus)}</div>
                <div className="text-gray-500">
                  {s.attendancePercentage === null || s.attendancePercentage === undefined ? '—' : `${s.attendancePercentage}%`} att.
                </div>
                <div className="flex items-center gap-1 text-gray-500">
                  <span>Last present: {formatDate(s.lastPresentDate)}</span>
                  {s.lastPresentDate && (
                    <span className="inline-block rounded-full bg-gray-100 px-1 py-0.5 font-semibold text-gray-500">
                      {daysSince(s.lastPresentDate)}d ago
                    </span>
                  )}
                </div>
              </td>
              {batch.phases.map((phase) => {
                const data = s.marksByPhase ? s.marksByPhase[phase.phaseId] : null;
                return (
                  <td
                    key={phase.phaseId}
                    className="border border-gray-400 px-1 py-1 text-center font-semibold"
                    style={data?.marks != null ? attemptStyle(data.attempt) : undefined}
                  >
                    {formatMarks(data?.marks)}
                    {attemptLabel(data?.attempt) && <div className="font-normal">{attemptLabel(data.attempt)}</div>}
                    {data?.examAttemptDatetime && <div className="font-normal">✓</div>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function DiplomaCompletionPrintView() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [courses, setCourses] = useState([]);
  const [generatedAt, setGeneratedAt] = useState(null);

  const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const courseIdsParam = searchParams.get('courseIds') || 'all';
  const batchStatusParam = searchParams.get('batchStatus') || 'ongoing';

  useEffect(() => {
    let isMounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const accessRes = await axiosInstance.get('/admin/me/coordinate-courses').catch(() => ({ data: {} }));
        const accessData = accessRes?.data || {};
        const isSuperAdmin = !!accessData.isSuperAdmin;
        const isManagement = String(AuthService.getUser()?.superAdmin?.staff?.staffType || '').trim().toLowerCase() === 'management';
        const accessibleCourseIds = Array.isArray(accessData.accessibleCourseIds) ? accessData.accessibleCourseIds : [];

        const accessibleParam = isSuperAdmin || isManagement
          ? 'all'
          : accessibleCourseIds.map((id) => Number(id)).filter((num) => Number.isFinite(num) && num > 0);

        const resp = await axiosInstance.get('/admin/dashboard/diploma-completion-report', {
          params: {
            accessibleCourses: accessibleParam,
            courseIds: courseIdsParam,
            batchStatus: batchStatusParam,
          },
        });
        if (!isMounted) return;

        const data = resp?.data || {};
        setCourses(data.courses || []);
        setGeneratedAt(data.generatedAt || new Date().toISOString());
      } catch (err) {
        if (isMounted) setError(err?.response?.data?.message || 'Failed to load diploma completion report');
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (loading || error) return;
    const timer = setTimeout(() => window.print(), 400);
    return () => clearTimeout(timer);
  }, [loading, error]);

  return (
    <>
      <style>{PRINT_STYLES}</style>
      <div className="min-h-screen bg-gray-100">
        <div className="print-hide sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-3 shadow-sm">
          <span className="text-sm font-medium text-gray-600">Print preview — Diploma Completion Report</span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => window.print()}
              className="rounded-lg border border-emerald-600 bg-emerald-600 px-4 py-2 text-xs font-medium text-white hover:bg-emerald-700"
            >
              Print
            </button>
            <button
              type="button"
              onClick={() => window.close()}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>

        <div id="diploma-completion-print" className="mx-auto max-w-6xl bg-white p-10 shadow-sm print:shadow-none">
          {loading ? (
            <div className="flex flex-col items-center justify-center gap-3 p-16 text-center">
              <svg className="h-9 w-9 animate-spin text-emerald-600" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <p className="text-sm font-medium text-gray-500">Loading report for print…</p>
            </div>
          ) : error ? (
            <p className="text-center text-red-600">{error}</p>
          ) : (
            <>
              {courses.length === 0 && (
                <p className="text-center text-sm text-gray-500">No batches match the applied filters.</p>
              )}
              {courses.map((course) => {
                const courseStudentCount = course.batches.reduce((sum, b) => sum + b.students.length, 0);
                const courseBatchNames = course.batches
                  .map((b) => b.batchInternalName || b.whatsappGroupName || b.batchName || 'Batch')
                  .join(', ');
                return (
                <div key={course.courseId ?? course.courseName} className="print-course-block">
                  <div className="border-b-2 border-gray-800 pb-4 text-center">
                    <h1 className="text-2xl font-black tracking-wide text-gray-900">ESOFT METRO CAMPUS BATTICALOA</h1>
                    <p className="mt-0.5 text-xs uppercase tracking-widest text-gray-500">Academic Affairs Division</p>
                  </div>

                  <div className="mt-5 text-center">
                    <span className="inline-block rounded-sm border border-gray-800 px-4 py-1 text-xs font-bold uppercase tracking-widest text-gray-800">
                      Official Notice
                    </span>
                    <h2 className="mt-3 text-xl font-bold text-gray-900">Diploma Completion Report</h2>
                    <p className="mt-1 text-base font-semibold text-gray-800">
                      {course.courseShortName || course.courseName || 'Unknown Course'}
                      {formatScheduleType(course.scheduleType) && ` (${formatScheduleType(course.scheduleType)})`}
                    </p>
                  </div>

                  <div className="mt-5 grid grid-cols-2 gap-x-6 gap-y-1 border border-gray-300 px-4 py-3 text-xs text-gray-700">
                    <div><span className="font-semibold">Generated:</span> {generatedAt ? new Date(generatedAt).toLocaleString('en-GB') : '—'}</div>
                    <div><span className="font-semibold">Students listed:</span> {courseStudentCount}</div>
                    <div>
                      <span className="font-semibold">Batch stage:</span>{' '}
                      {batchStatusParam === 'all' ? 'All stages' : formatStatusLabel(batchStatusParam)}
                    </div>
                    <div>
                      <span className="font-semibold">Batches covered:</span> {course.batches.length}
                    </div>
                    <div className="col-span-2">
                      <span className="font-semibold">Batch names:</span> {courseBatchNames || '—'}
                    </div>
                  </div>

                  <div className="mt-6 space-y-5">
                    {course.batches.slice(0, -1).map((batch) => (
                      <BatchPhaseSheetTable key={batch.batchId} batch={batch} />
                    ))}

                    <div className="print-signoff-group space-y-5">
                      {course.batches.length > 0 && (
                        <BatchPhaseSheetTable
                          key={course.batches[course.batches.length - 1].batchId}
                          batch={course.batches[course.batches.length - 1]}
                        />
                      )}

                      <div className="mt-8 grid grid-cols-2 gap-10 text-xs text-gray-600">
                        <div>
                          <div className="h-16"></div>
                          <div className="border-t border-gray-800 pt-1">Prepared by — Signature &amp; Date</div>
                        </div>
                        <div>
                          <div className="h-16"></div>
                          <div className="border-t border-gray-800 pt-1">Head of Department — Signature &amp; Date</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </>
  );
}
